/* lib/adapters/ttk.js — форма данных ТТК между боевым API и UI прототипа.
 * Реестр (этот файл) — brief. Detail/row/payload-мапперы добавим при подключении редактора.
 * Decimal-as-string → Number; target_kind 'sub_recipe'↔'ttk' (для редактора). */

export const num = (v) => (v == null || v === '' ? null : Number(v));

// TTKListSerializer → форма карточки реестра «Рецепты».
// Поля 1:1 по именам; коэрцим decimal-строки в числа (для сортировки по цене/ккал).
export function fromApiTTKBrief(t) {
  return {
    id: t.id,
    kind: t.kind, // 'dish' | 'semiproduct'
    title: t.title,
    status: t.status, // 'ready' | 'unbound' | 'stub'
    kcal_per_portion: num(t.kcal_per_portion),
    output_g: num(t.output_g),
    cost_per_portion_rub: num(t.cost_per_portion_rub),
    cost_status: t.cost_status,
    allergens: Array.isArray(t.allergens) ? t.allergens : [],
    menu_ids: Array.isArray(t.menu_ids) ? t.menu_ids : [],
  };
}

// ── Detail (редактор ТТК) ─────────────────────────────────────────────
const round1 = (v) => (v == null ? null : Math.round(v * 10) / 10);

// Строка: backend target_kind 'card'|'sub_recipe'|'unbound' → UI 'card'|'ttk'|null.
export function fromApiRow(r) {
  const tk = r.target_kind === 'sub_recipe' ? 'ttk' : (r.target_kind === 'card' ? 'card' : null);
  return {
    id: r.id,
    position: r.position,
    name_raw: r.name_raw || '',
    target_kind: tk,
    target_id: r.target_id ?? null,
    target_label: r.target_label || r.name_raw || '',
    amount_brutto_g: num(r.amount_brutto_g),
    amount_netto_g: num(r.amount_netto_g),
    weight_in_dish_g: num(r.weight_in_dish_g),
    unit: r.unit || 'г',
    notes: r.notes || '',
  };
}

// Статус ТТК из detail (в detail-сериализаторе нет плоского status, как в list).
function deriveStatus(d) {
  if (d.is_stub) return 'stub';
  const summary = d.ingredient_rows_summary || {};
  if ((summary.unbound || 0) > 0) return 'unbound';
  return 'ready';
}

// TTKDetailSerializer (+ used-in) → форма detail, которую ждёт TTKEditor.
// GOST-поля — верхнеуровневые зеркала; technology — из draft_json; kbju_per_portion — из calculated_json.
export function fromApiTTKDetail(d, usedIn) {
  const draft = (d.draft_json && typeof d.draft_json === 'object') ? d.draft_json : {};
  const calc = (d.calculated_json && typeof d.calculated_json === 'object') ? d.calculated_json : {};
  const parents = (usedIn && usedIn.parents) || [];
  const menus = (usedIn && (usedIn.menus || usedIn.in_menus)) || [];
  return {
    id: d.id,
    title: d.title,
    kind: d.kind,
    status: deriveStatus(d),
    is_stub: !!d.is_stub,
    output_g: num(d.output_g),
    selling_price_rub: num(d.selling_price_rub),
    ingredient_rows: (d.ingredient_rows || []).map(fromApiRow),
    // ГОСТ-разделы (верхний уровень сериализатора)
    application_scope: d.application_scope || '',
    raw_material_requirements: d.raw_material_requirements || '',
    plating_and_service: d.plating_and_service || '',
    storage_conditions: d.storage_conditions || '',
    appearance: d.appearance || '',
    texture: d.texture || '',
    taste: d.taste || '',
    smell: d.smell || '',
    microbiology: d.microbiology || '',
    // Технология — в draft_json (нет верхнеуровневого поля). Два формата:
    // СТРОКА (ручная правка через UpdateDraftSerializer) и МАССИВ шагов
    // (LLM-генерация: tree-job/чат пишут draft.technology списком — раньше
    // массив молча терялся и поле в редакторе было пустым).
    technology: Array.isArray(draft.technology)
      ? draft.technology.filter((s) => typeof s === 'string' && s.trim()).join('\n')
      : (typeof draft.technology === 'string' ? draft.technology : ''),
    // КБЖУ на порцию — из calculated_json (объект B/Ж/У), нередактируемо
    kbju_per_portion: (calc.kbju_per_portion && typeof calc.kbju_per_portion === 'object') ? calc.kbju_per_portion : null,
    // эффективные/авто аллергены (сервер)
    allergens: Array.isArray(d.allergens) ? d.allergens : [],
    allergens_auto: Array.isArray(d.allergens_auto) ? d.allergens_auto : [],
    // серверные cost-поля (число)
    cost_per_portion_rub: num(d.cost_per_portion_rub),
    cost_per_100g_rub: num(d.cost_per_100g_rub),
    cost_status: d.cost_status,
    cost_missing_count: d.cost_missing_count,
    kcal_per_portion: num(d.kcal_per_portion),
    // used-in
    parents, menus,
    // сырьё для save (merge draft) и отладки
    _draft_json: draft,
    _kbju_per_100g: (calc.kbju_per_100g && typeof calc.kbju_per_100g === 'object') ? calc.kbju_per_100g : null,
  };
}

// SERVER-derived: форма как у ttkCompute.recompute, но cost/allergens — с сервера,
// а потери/fc%/КБЖУ-на-100г считаем из величин (карточные данные не нужны).
export function serverDerived(detail) {
  const out = num(detail.output_g);
  const rows = (detail.ingredient_rows || []).map((r) => {
    const b = num(r.amount_brutto_g), n = num(r.amount_netto_g), w = num(r.weight_in_dish_g);
    return {
      ...r,
      cold_loss_pct: (b != null && n != null && b > 0) ? round1(((b - n) / b) * 100) : null,
      hot_loss_pct: (n != null && w != null && n > 0) ? round1(((n - w) / n) * 100) : null,
      cost_rub: null, // бэк не отдаёт per-row cost; итог — серверный
    };
  });
  const cost = detail.cost_per_portion_rub;
  const price = detail.selling_price_rub;
  const fc_pct = (cost != null && price != null && price > 0) ? round1((cost / price) * 100) : null;
  let kbju_per_100g = detail._kbju_per_100g;
  if (!kbju_per_100g && detail.kbju_per_portion && out) {
    const k = (v) => (v == null ? null : round1((Number(v) / out) * 100));
    const p = detail.kbju_per_portion;
    kbju_per_100g = { kcal: k(p.kcal), protein: k(p.protein), fat: k(p.fat), carbs: k(p.carbs) };
  }
  return {
    ingredient_rows: rows,
    cost_per_portion_rub: detail.cost_per_portion_rub,
    cost_per_100g_rub: detail.cost_per_100g_rub,
    cost_status: detail.cost_status,
    cost_missing_count: detail.cost_missing_count,
    fc_pct,
    kbju_per_100g: kbju_per_100g || null,
    allergens_auto: detail.allergens_auto || [],
  };
}

// Пустой detail для создания (kind: 'dish'|'semiproduct').
export function blankTTKDetail(kind = 'dish') {
  return {
    id: null, title: '', kind, status: 'stub', is_stub: true,
    output_g: null, selling_price_rub: null,
    ingredient_rows: [],
    application_scope: '', raw_material_requirements: '', plating_and_service: '',
    storage_conditions: '', appearance: '', texture: '', taste: '', smell: '', microbiology: '',
    technology: '', kbju_per_portion: null,
    allergens: [], allergens_auto: [],
    cost_per_portion_rub: null, cost_per_100g_rub: null, cost_status: 'pending', cost_missing_count: 0,
    kcal_per_portion: null, parents: [], menus: [], _draft_json: {}, _kbju_per_100g: null,
  };
}

// detail (UI) → PATCH-payload. GOST/technology/allergens пишутся через draft_json (мёрж в существующий),
// output_g/selling/title/kind — верхним уровнем. ingredient_rows тут НЕ передаём (granular endpoints).
const DRAFT_TEXT_KEYS = [
  'application_scope', 'raw_material_requirements', 'plating_and_service', 'storage_conditions',
  'appearance', 'texture', 'taste', 'smell', 'microbiology', 'technology',
];
export function toTTKPayload(detail) {
  // UpdateDraftSerializer: {title, kind, selling_price_rub, draft:{...TTKDraft}}.
  // ГОСТ-текст/technology/allergens/output_g — ВНУТРИ draft (НЕ верхним уровнем, НЕ draft_json).
  const draft = { ...(detail._draft_json || {}) };
  // Состав редактируется ТОЛЬКО через /rows/ (granular endpoints), а не через draft.
  // Спред _draft_json мог затащить сюда draft_json.ingredients (в т.ч. устаревший/
  // неполный после правок строк — напр. пустые {} от отвязанных строк). На бэке
  // непустой ingredients валидируется как ПОЛНАЯ TTKDraft и роняет сохранение
  // («Неверная структура draft»). Поэтому состав/стаб здесь не отправляем —
  // патчим только текст/technology/allergens/output_g (partial-merge на бэке).
  delete draft.ingredients;
  delete draft._stub;
  for (const k of DRAFT_TEXT_KEYS) draft[k] = detail[k] || '';
  draft.allergens = Array.isArray(detail.allergens) ? detail.allergens : [];
  if (detail.output_g != null) draft.output_g = Number(detail.output_g);
  const body = {
    title: (detail.title || '').trim(),
    kind: detail.kind,
    draft,
  };
  if (detail.selling_price_rub != null) body.selling_price_rub = Number(detail.selling_price_rub);
  return body;
}
